import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import "bootstrap-icons/font/bootstrap-icons.css";

import { activateAccount } from "../../apis/activateAccount";
import { AccountLockedError, getApiErrorDetail } from "../../utils/authErrors";
import { setAuthSession } from "../../utils/authSession";
import resolvePostAuthPath from "../../utils/resolvePostAuthPath";
import { APP_ROUTES } from "../../constants/appRoutes";
import Input from "../campaign/wizard/Input";
import OtpInput from "../shared/OtpInput";

export default function ActivateForm({
    email: initialEmail = "",
    onAuthenticated = null,
    onAuthSuccess = null,
    onSwitchToLogin = null,
}) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [email, setEmail] = useState(initialEmail);
    const [activationCode, setActivationCode] = useState("");
    const [warning, setWarning] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const completeActivation = async () => {
        if (onAuthSuccess) {
            await onAuthSuccess();
            return;
        }

        if (onAuthenticated) {
            onAuthenticated();
            return;
        }

        const path = await resolvePostAuthPath(null);
        navigate(path);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setWarning("");

        if (activationCode.length !== 6) {
            toast.error("Please enter the 6-digit activation code.");
            return;
        }

        setSubmitting(true);
        try {
            const result = await activateAccount({ email, activation_code: activationCode });
            setAuthSession(dispatch, result.user, result.access_token, { isNewUser: true });
            toast.success("Account activated successfully.");
            await completeActivation();
        } catch (err) {
            if (err instanceof AccountLockedError) {
                navigate(APP_ROUTES.ACCOUNT_LOCKED);
                return;
            }
            const detail = getApiErrorDetail(err);
            if (detail.includes("expired")) {
                setWarning(
                    "Your verification code has expired. Please contact support to resend your code."
                );
            } else if (detail.includes("attempt")) {
                setWarning(detail);
            } else {
                toast.error(detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <p className="mb-4 text-center text-xs text-slate-500">
                Didn&apos;t receive the email? Contact support 
            </p>

            <form onSubmit={handleSubmit}>
                <Input
                    label="Email address"
                    labelIcon="envelope"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus={!email}
                    wrapperClassName="mb-5"
                />

                <div className="mb-5">
                    <p className="form-label mb-3 text-center text-xs font-bold uppercase tracking-[0.12em] text-dark/90">
                        Activation code
                    </p>
                    <OtpInput
                        value={activationCode}
                        onChange={setActivationCode}
                        disabled={submitting}
                        autoFocus={!!email}
                    />
                </div>

                {warning ? (
                    <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        {warning}
                    </p>
                ) : null}

                <button
                    type="submit"
                    className="btn btn-primary-app w-full font-semibold"
                    disabled={submitting}
                >
                    {submitting ? "Activating…" : "Activate account"}
                </button>
            </form>

            {onSwitchToLogin ? (
                <p className="mt-5 text-center text-sm text-slate-600">
                    Already activated?{" "}
                    <button
                        type="button"
                        className="border-0 bg-transparent p-0 font-semibold text-slate-900 hover:text-slate-700"
                        onClick={onSwitchToLogin}
                    >
                        Log in
                    </button>
                </p>
            ) : null}
        </>
    );
}
